import React, { useState, useRef, useEffect } from 'react';
import Message from './Message';
import './App.css';

function ChatApp() {
    // Function to generate a UUID
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
      };
    
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    // const [isTyping, setIsTyping] = useState(false); // Typing indicator state
    const [isThinking, setIsThinking] = useState(false); // Thinking indicator state
    const [isExpanded, setIsExpanded] = useState(false); // State for expanding the chat container

    // Use sessionStorage instead of localStorage for threadId
    const [threadId] = useState(() => {
      let savedThreadId = sessionStorage.getItem('threadId');
      if (!savedThreadId) {
        savedThreadId = generateUUID();
        sessionStorage.setItem('threadId', savedThreadId);
      }
      return savedThreadId;
    });
  
    const chatContainerRef = useRef(null);

    // Conversation Starter Messages
    const starterMessages = [
      "What has Chandler written about Zion National Park?",
      "Did Chandler write about Ray Dalio's Great Power Index in 2024?",
      "Tell me about Apple revenue from 2020 - 2023",
      "How has Microsoft described Azure competition over the past 5 years?",
    ];

    const hasUserMessages = messages.some(msg => msg.type === 'user');

    // Function to get CSRF token from cookies (if needed)
    const getCookie = (name) => {
      let cookieValue = null;
      if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].trim();
          if (cookie.substring(0, name.length + 1) === name + '=') {
            cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
            break;
          }
        }
      }
      return cookieValue;
    };
  
    const csrftoken = getCookie('csrftoken');

    // Function to send a message
    const sendMessage = async () => {
      const message = inputValue.trim();
      if (message) {
        // Display the user's message
        const userMessage = { type: 'user', content: message };
        setMessages((prevMessages) => [...prevMessages, userMessage ]);

        // Clear the input field
        setInputValue('');

        // Create the bot's message placeholder
        const botMessage = { type: 'bot', content: '' };
        setMessages((prevMessages) => [...prevMessages, botMessage]);

        // Show the thinking indicator
        setIsThinking(true); // Show typing indicator

        // Send the message to the server
        try {
          const response = await fetch('https://sp500-backendsep2024-39991682437.us-central1.run.app/chat/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrftoken,
            },
            body: JSON.stringify({ query: message, thread_id: threadId }),
          });

          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let result = '';

          // Function to read the stream
          const read = async () => {
            const { done, value } = await reader.read();
            if (done) {
              // Stream complete
              setIsThinking(false); // Hide typing indicator once done
              return;
            }

            // Decode the chunk and append to result
            const chunk = decoder.decode(value, { stream: true });
            result += chunk;

            // Update the bot's message content
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              // Update the last message (bot's message)
              updatedMessages[updatedMessages.length - 1].content = result;
              return updatedMessages;
            });

            // Scroll to the latest message
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

            // Read the next chunk
            await read();
          };

          // Start reading the stream
          await read();
        } catch (error) {
          console.error('Error:', error);
          // Handle error
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1].content =
              "I'm sorry, I encountered an error while processing your request.";
            return updatedMessages;
          });
          setIsThinking(false); // Hide typing indicator if there's an error
        }
      }
    };

    // Handle starter message click
    const handleStarterClick = (starter) => {
      setInputValue(starter);
      sendMessage();
    };

    // Allow pressing "Enter" to send a message
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        sendMessage();
      }
    };

    // Scroll to the bottom every time messages change
    useEffect(() => {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      
      // Expand the chatbox if messages are long enough
      if (messages.length > 2) { // Adjust the number 2 to decide when the box should expand
        setIsExpanded(true);
      }
    }, [messages]);

    return (
      <div id="chat-container" className={isExpanded ? 'expanded' : ''}> {/* Apply 'expanded' class conditionally */}
        <div id="chat-messages" ref={chatContainerRef}>
          {!hasUserMessages && (
            <div className="starter-messages">
              <p>Start a conversation with Sydney:</p>
              <div className="starter-buttons">
                {starterMessages.map((starter, index) => (
                  <button
                    key={index}
                    className="starter-button"
                    onClick={() => handleStarterClick(starter)}
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, index) => (
            <Message key={index} message={msg} />
          ))}
          {isThinking && <div className="thinking-indicator">Sydney is thinking and working on your question...</div>}
        </div>
        <div id="input-area">
          <input
            type="text"
            id="user-input"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <button id="send-button" onClick={sendMessage}>
            Send
          </button>
        </div>
      </div>
    );
  }

  export default ChatApp;
